import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Check, ShieldCheck, CreditCard, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";

export default function Checkout() {
  const [params] = useSearchParams();
  const planSlug = params.get("plan") || "trial";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1=details, 2=paying, 3=done
  const [paidInfo, setPaidInfo] = useState(null);

  useEffect(() => {
    api.get("/plans")
      .then((r) => setPlan(r.data.find((p) => p.slug === planSlug) || r.data[0]))
      .catch((err) => { console.warn("plans fetch failed:", err?.message); });
  }, [planSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      navigate("/login", { state: { from: `/checkout?plan=${planSlug}` } });
      return;
    }
    if (!plan) return;
    setProcessing(true);
    setStep(2);
    try {
      // 1. Create razorpay order (mocked)
      const { data: order } = await api.post("/orders/create", { plan_slug: plan.slug, amount: plan.price });
      // 2. Simulate razorpay checkout (mocked)
      await new Promise((r) => setTimeout(r, 1400));
      const { data: verify } = await api.post("/orders/verify", {
        razorpay_order_id: order.razorpay_order_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
      });
      // 3. Create subscription
      const { data: sub } = await api.post("/subscriptions", {
        plan_slug: plan.slug,
        address, pincode, start_date: startDate,
      });
      setPaidInfo({ order, verify, sub });
      setStep(3);
    } catch (e) {
      alert("Payment failed: " + (e.response?.data?.detail || e.message));
      setStep(1);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="bg-dd-page min-h-screen" data-testid="checkout-page">
      <Navbar />
      <div className="pt-28 dd-container max-w-5xl pb-20">
        <Link to="/#plans" className="text-sm text-dd-ink-muted hover:text-dd-green">← Back to plans</Link>
        <h1 className="font-serif text-4xl md:text-5xl text-dd-ink mt-4">Complete your subscription</h1>

        {!plan ? (
          <div className="mt-10 text-dd-ink-muted">Loading plan…</div>
        ) : step === 3 ? (
          <div className="mt-10 bg-white border border-dd-green rounded-sm p-10 text-center" data-testid="checkout-success">
            <div className="w-14 h-14 rounded-full bg-dd-green text-dd-page flex items-center justify-center mx-auto">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="mt-5 font-serif text-3xl text-dd-ink">You're all set!</h2>
            <p className="mt-2 text-dd-ink-muted">Your {plan.name} is active. First delivery: {startDate}.</p>
            <div className="mt-3 text-xs text-dd-ink-muted font-mono">Payment: {paidInfo?.verify?.payment_id} (MOCKED)</div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="bg-dd-green text-dd-page px-6 py-3 rounded-sm font-medium">Go to Dashboard</Link>
              <Link to="/" className="border border-dd-line px-6 py-3 rounded-sm font-medium">Back to Home</Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-5 gap-8 mt-10">
            <form onSubmit={submit} className="md:col-span-3 bg-white border border-dd-line rounded-sm p-7 space-y-5" data-testid="checkout-form">
              <h2 className="font-serif text-2xl text-dd-ink">Delivery details</h2>
              <div>
                <label className="dd-label text-dd-ink-muted block mb-2">Address</label>
                <textarea required value={address} onChange={(e) => setAddress(e.target.value)} rows={3}
                  placeholder="Flat / Building / Street / Society"
                  className="w-full bg-dd-page border border-dd-line rounded-sm px-4 py-3 focus:border-dd-green focus:ring-1 focus:ring-dd-green outline-none"
                  data-testid="checkout-address" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="dd-label text-dd-ink-muted block mb-2">Pincode</label>
                  <input required value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                    className="w-full bg-dd-page border border-dd-line rounded-sm px-4 py-3 focus:border-dd-green focus:ring-1 focus:ring-dd-green outline-none"
                    data-testid="checkout-pincode" />
                </div>
                <div>
                  <label className="dd-label text-dd-ink-muted block mb-2">Start Date</label>
                  <input required type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)}
                    className="w-full bg-dd-page border border-dd-line rounded-sm px-4 py-3 focus:border-dd-green focus:ring-1 focus:ring-dd-green outline-none"
                    data-testid="checkout-date" />
                </div>
              </div>

              <div className="rounded-sm border border-dd-gold/40 bg-dd-gold-light/20 p-4 flex items-start gap-3 text-sm">
                <ShieldCheck className="w-5 h-5 text-dd-gold-dark shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-dd-ink">Secure checkout (MOCKED)</div>
                  <div className="text-dd-ink-muted">Razorpay is running in simulation mode. No real payment is charged.</div>
                </div>
              </div>

              <button type="submit" disabled={processing}
                className="w-full bg-dd-green text-dd-page py-3.5 rounded-sm font-medium hover:bg-dd-green-light transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="checkout-pay-btn">
                {processing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>) : (<><CreditCard className="w-4 h-4" /> Pay ₹{plan.price}</>)}
              </button>
            </form>

            <aside className="md:col-span-2 bg-dd-alt border border-dd-line rounded-sm p-7 h-fit">
              <div className="dd-label text-dd-gold-dark">Order Summary</div>
              <h3 className="font-serif text-2xl text-dd-ink mt-2">{plan.name}</h3>
              <p className="text-sm text-dd-ink-muted">{plan.tagline}</p>
              <div className="mt-5 text-sm space-y-2 border-t border-dd-line pt-4">
                <div className="flex justify-between"><span className="text-dd-ink-muted">Duration</span><span>{plan.duration}</span></div>
                <div className="flex justify-between"><span className="text-dd-ink-muted">Subtotal</span><span>₹{plan.price}</span></div>
                <div className="flex justify-between"><span className="text-dd-ink-muted">Delivery</span><span className="text-dd-green">Free</span></div>
              </div>
              <div className="mt-4 flex justify-between text-lg border-t border-dd-line pt-4">
                <span className="font-medium">Total</span>
                <span className="font-serif text-2xl text-dd-green">₹{plan.price}</span>
              </div>
            </aside>
          </div>
        )}
      </div>
      <Footer />
      <WhatsAppFAB plan={planSlug} />
    </div>
  );
}
